import React, {
  useState,
  useEffect,
  ReactNode,
  createContext,
  useContext,
} from "react";
import * as Sentry from "@sentry/react";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import { Box } from "@mui/material";
import { SUPPORT_EMAIL } from "@/constants";

interface ErrorBoundaryProps {
  children: ReactNode;
}

const ErrorBoundaryContext = createContext<
  (error: Error, errorInfo?: React.ErrorInfo) => void
>(() => {});

export const useErrorBoundary = () => useContext(ErrorBoundaryContext);

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    // Sentry.captureException(error, { extra: errorInfo as any });
    setHasError(true);
  };

  const handleClose = () => {
    setHasError(false);
  };

  useEffect(() => {
    // Only react to real runtime Errors, ignore resource loading errors
    const errorHandler = (event: ErrorEvent) => {
      if (event?.error instanceof Error) {
        handleError();
      }
    };

    // Capture unhandled promise rejections safely
    const rejectionHandler = (event: PromiseRejectionEvent) => {
      const reason = (event && (event as any).reason) as unknown;
      if (reason instanceof Error) {
        handleError();
      }
    };

    window.addEventListener("error", errorHandler);
    window.addEventListener("unhandledrejection", rejectionHandler);

    return () => {
      window.removeEventListener("error", errorHandler);
      window.removeEventListener("unhandledrejection", rejectionHandler);
    };
  }, []);

  return (
    <ErrorBoundaryContext.Provider value={handleError}>
      {hasError && (
        <Snackbar open={hasError} autoHideDuration={6000} onClose={handleClose}>
          <Alert onClose={handleClose} severity="error">
            An error has occurred. Please contact support at{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              style={{ color: "#fff", textDecoration: "underline" }}
            >
              {SUPPORT_EMAIL}
            </a>{" "}
            or try again later.
          </Alert>
        </Snackbar>
      )}
      {children}
    </ErrorBoundaryContext.Provider>
  );
};


export default ErrorBoundary;
// export default Sentry.withErrorBoundary(ErrorBoundary, {
//   fallback: (
//     <Box sx={{ display: "flex", justifyContent: "center" }}>
//       An error has occurred. Please contact support at
//       <a href={`mailto:${SUPPORT_EMAIL}`} style={{ margin: "0 5px" }}>
//         {SUPPORT_EMAIL}
//       </a>
//       or try again later.
//     </Box>
//   ),
// });
